/// <amd-module name="Test/ManageTestPlans/Sources/TestPlanDirectorySource" />
import {
    IAllTestPlanPayload,
    IAllTestPlanInitialPayload,
    IMyFavoriteTestPlanPayload,
    IMyTestPlanPayload,
    IMyTestPlanSkinnyPayload,
    ITestPlan
} from "Test/ManageTestPlansCommon/Contracts";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { WorkItem, WorkItemExpand, WorkItemErrorPolicy } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { IVssPerformanceService, IVssScenario } from "VSS/Platform/Performance";
import { TcmPerfScenarios, TelemetryConstants, WorkItemField } from "Test/ManageTestPlansCommon/Constants";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export class TestPlanDirectorySource {
    public static getInstance() {
        if (!TestPlanDirectorySource._instance) {
            TestPlanDirectorySource._instance = new TestPlanDirectorySource();
        }
        return TestPlanDirectorySource._instance;
    }

    public init(context: IVssPageContext) {
        this._pageContext = context;
    }

    public getMyFavoriteTestPlanData(): Promise<IMyFavoriteTestPlanPayload> {
        return this._getData<IMyFavoriteTestPlanPayload>(this.myFavoriteTestPlansDataProviderId);
    }

    public getMySkinnyTestPlanData(): Promise<IMyTestPlanSkinnyPayload> {
        return this._getData<IMyTestPlanSkinnyPayload>(this.myTestPlanSkinnyDataProviderId, true, TcmPerfScenarios.FetchTestPlansMySkinnyPageData);
    }

    public getMyTestPlanPageData(): Promise<IMyTestPlanPayload> {
        return this._getData<IMyTestPlanPayload>(this.myTestPlanDataProviderId, true, TcmPerfScenarios.FetchTestPlansMinePageData);
    }

    /**
     * get data provider value for all page
     */
    public getAllTestPlanPageData(): Promise<IAllTestPlanPayload> {
        let dataProviderId: string = this._isAllTestPlanSkinnyProviderEnabled() ? this.allTestPlanDataProviderId : this.allTestPlanDataProviderOldId;
        return this._getData<IAllTestPlanPayload>(dataProviderId, true, TcmPerfScenarios.FetchTestPlansAllPageData, true);
    }

    /**
     * get initial data provider value for all page
     */
    public getAllTestPlanInitialPageData(): Promise<IAllTestPlanPayload> {
        return this._getData<IAllTestPlanPayload>(this.allTestPlanInitialDataProviderId, false, "", true);
    }

    /**
     * get test plan meta data using wit calls in batches
     * @param idsToFetch
     */
    public getTestPlanMetaData(idsToFetch: number[]): Promise<{ [key: string]: ITestPlan }> {
        return new Promise((resolve, reject) => {
            this._getWorkItemContent().then(
                () => {
                    this._getWorkItems(idsToFetch).then(
                        (workItems: WorkItem[]) => {
                            resolve(this._createTestPlanMap(workItems));
                        },
                        (error: Error) => {
                            reject(error);
                        }
                    );
                },
                (error: Error) => {
                    reject(error);
                }
            );
        });
    }

    private _getWorkItems(idsToFetch: number[]): Promise<WorkItem[]> {
        let columns: string[] = [];
        columns.push(WorkItemField.iterationPath);
        columns.push(WorkItemField.title);
        columns.push(WorkItemField.testPlanId);
        columns.push(WorkItemField.workItemState);
        columns.push(WorkItemField.assignedTo);
        columns.push(WorkItemField.areaPath);
        columns.push(WorkItemField.workItemType);

        const witClient = this._pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");

        let workItemsPromise = witClient.getWorkItemsBatch({
            ids: idsToFetch,
            fields: columns,
            $expand: WorkItemExpand.None,
            asOf: new Date(),
            errorPolicy: WorkItemErrorPolicy.Omit
        }, this._getProjectIdFromContext());

        return new Promise<WorkItem[]>((resolve, reject) => {
            workItemsPromise.then(
                (workItems: WorkItem[]) => {
                    resolve(workItems);
                },
                (error: Error) => {
                    reject(error);
                }
            );
        });
    }

    private _createTestPlanMap(workitems: WorkItem[]): { [key: string]: ITestPlan } {
        let testPlanMap: { [key: string]: ITestPlan } = {};

        workitems.forEach(workItem => {
            const testplan: ITestPlan = {
                id: workItem.id,
                loaded: true,
                name: workItem.fields[WorkItemField.title],
                fields: {
                    testPlanId: workItem.fields[WorkItemField.testPlanId],
                    areaPath: workItem.fields[WorkItemField.areaPath],
                    iterationPath: workItem.fields[WorkItemField.iterationPath],
                    state: workItem.fields[WorkItemField.workItemState],
                    assignedTo: workItem.fields[WorkItemField.assignedTo] &&
                        workItem.fields[WorkItemField.assignedTo].id && {
                        id: workItem.fields[WorkItemField.assignedTo].id,
                        displayName: workItem.fields[WorkItemField.assignedTo].displayName,
                        uniqueName: workItem.fields[WorkItemField.assignedTo].uniqueName,
                        imageUrl: workItem.fields[WorkItemField.assignedTo].imageUrl
                    }
                }
            };

            testPlanMap[workItem.id.toString()] = testplan;
        });

        return testPlanMap;
    }

    private get _service() {
        return this._pageContext.getService<IVssContributionService>("IVssContributionService");
    }

    /**
     * Get project id from context
     */
    private _getProjectIdFromContext(): string {
        const tfsPageData = this._pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        if (tfsPageData && tfsPageData.project) {
            const project = tfsPageData.project;
            return project.id;
        } else {
            return "";
        }
    }

    private _getWorkItemContent(): Promise<void> {
        if (!this._workItemContentPromise) {
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            this._workItemContentPromise = contributionService.getContributionAsync("ms.vss-work-web.common-content");
        }

        return this._workItemContentPromise;
    }

    private get _perfService() {
        return this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }

    private _isAllTestPlanSkinnyProviderEnabled(): boolean {
        return isFeatureFlagEnabled(this._pageContext, c_allTestPlanSkinnyProviderFF, false);
    }

    private _getData<T>(
        contributionId: string,
        instrumentPerf: boolean = false,
        perfMarker: string = "",
        ignoreCache: boolean | undefined = undefined
    ): Promise<T> {
        return new Promise((resolve, reject) => {
            let errorCallback = (error: Error) => {
                reject(error);
            };

            let pageData: T | undefined = this._service.getData<T>(contributionId);
            if (pageData) {
                resolve(pageData);
            } else {
                // Get page data from the data provider
                const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");

                // Measuring the time taken for the call made from the client to fetch the test plans data for the "All" page.
                if (instrumentPerf && perfMarker) {
                    this._perfService.startScenario(perfMarker, false);
                }
                contributionService.getContributionAsync(contributionId).then(() => {
                    this._service.getDataAsync<T>(contributionId, undefined, ignoreCache).then((pageData: T) => {
                        if (instrumentPerf && perfMarker) {
                            this._perfService.endScenario(TelemetryConstants.areaTestManagement, perfMarker, false);
                        }
                        resolve(pageData);
                    }, errorCallback);
                }, errorCallback);
            }
        });
    }

    private _workItemContentPromise: Promise<void> | undefined;
    private _pageContext: IVssPageContext;
    private static _instance: TestPlanDirectorySource;
    private readonly batchSize: number = 200;
    private readonly allTestPlanDataProviderId: string = "ms.vss-test-web.testplan-hub-directory-all-tab-data-provider";
    private readonly allTestPlanDataProviderOldId: string = "ms.vss-test-web.testplan-hub-directory-all-tab-data-provider-old";
    private readonly myTestPlanDataProviderId: string = "ms.vss-test-web.testplan-hub-directory-mine-tab-data-provider";
    private readonly myFavoriteTestPlansDataProviderId: string = "ms.vss-test-web.testplan-hub-directory-myfavoriteplans-data-provider";
    private readonly myTestPlanSkinnyDataProviderId: string = "ms.vss-test-web.testplan-hub-directory-mine-tab-skinny-data-provider";
    private readonly allTestPlanInitialDataProviderId: string = "ms.vss-test-web.testplan-hub-directory-all-tab-initial-data-provider";
}

const c_allTestPlanSkinnyProviderFF = "WebAccess.TestManagement.AllTestPlanSkinnyProvider";
